/**
 * API クライアント。
 *
 * 参加者はモバイル回線を使うため、通信断を前提とした作りにしている。
 *   - 冪等キーは「解答の内容」に紐づけて保持し、再送時は同じキーを使う
 *   - GET は短いタイムアウトで自動リトライする
 *   - 失敗は必ず ApiError に変換し、画面はメッセージを表示するだけで済むようにする
 */

import type {
  AnswerResponse,
  HintResponse,
  JoinResponse,
  MeResponse,
  ParticipantView,
  QuestionsResponse,
  ScoreResponse,
} from "./types.js";

/** サーバーが返すエラー本文 */
interface ErrorBody {
  error: { code: string; message: string; params?: Record<string, string | number> };
}

export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly params: Record<string, string | number>;

  constructor(code: string, message: string, httpStatus: number, params = {}) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = httpStatus;
    this.params = params;
  }

  /** セッションが無効（QRの読み直しが必要） */
  get isUnauthorized(): boolean {
    return this.httpStatus === 401;
  }

  /** 通信そのものに失敗した（電波状況の問題の可能性が高い） */
  get isNetworkError(): boolean {
    return this.code === "E011" && this.httpStatus === 0;
  }
}

const NETWORK_ERROR_MESSAGE =
  "通信エラーが発生しました。電波状況を確認して、もう一度お試しください。";

function networkError(): ApiError {
  return new ApiError("E011", NETWORK_ERROR_MESSAGE, 0);
}

interface RequestOptions {
  method?: "GET" | "POST";
  body?: unknown;
  /** 失敗時に再試行する回数（GETのみ既定で有効） */
  retries?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  /**
   * 429（混雑）でも再試行するか。
   *
   * 通常、4xx はサーバーが意図して拒否しているため再試行しない。
   * 参加受付だけは例外とする。開始の合図で一斉に読み取ると混雑しうるが、
   * ここで諦めると参加者は「参加できない」まま取り残される。
   * 待って自動的にやり直せば、本人の操作なしに復帰できる。
   */
  retryOnBusy?: boolean;
  /** 混雑により待機に入ったことを画面へ伝える */
  onBusy?: (attempt: number) => void;
}

/** 混雑時に待つ時間。0.8秒、1.6秒、3.2秒…と延ばし、上限は8秒 */
function busyDelayMs(attempt: number): number {
  return Math.min(800 * 2 ** attempt, 8_000);
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const retries = options.retries ?? (method === "GET" ? 2 : 0);
  const timeoutMs = options.timeoutMs ?? 10_000;
  const busyRetries = options.retryOnBusy === true ? 6 : 0;

  let lastError: ApiError = networkError();
  let busyAttempts = 0;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    if (options.signal) {
      options.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }

    try {
      const response = await fetch(path, {
        method,
        headers: options.body === undefined ? {} : { "Content-Type": "application/json" },
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        // セッションCookieを送受信する
        credentials: "same-origin",
        signal: controller.signal,
      });

      if (response.ok) {
        return (await response.json()) as T;
      }

      let body: ErrorBody | null = null;
      try {
        body = (await response.json()) as ErrorBody;
      } catch {
        body = null;
      }

      const error = new ApiError(
        body?.error.code ?? "E011",
        body?.error.message ?? NETWORK_ERROR_MESSAGE,
        response.status,
        body?.error.params ?? {}
      );

      // 混雑（429）は時間をおけば通るため、指定されたルートだけ待って再試行する。
      // この分は retries を消費させず、専用の回数で数える。
      if (response.status === 429 && busyAttempts < busyRetries) {
        clearTimeout(timer);
        options.onBusy?.(busyAttempts);
        await new Promise((resolve) => setTimeout(resolve, busyDelayMs(busyAttempts)));
        busyAttempts++;
        attempt--;
        continue;
      }

      // 4xx はサーバーが意図して拒否しているため再試行しない
      if (response.status < 500) throw error;
      lastError = error;
    } catch (err) {
      if (err instanceof ApiError) throw err;
      lastError = networkError();
    } finally {
      clearTimeout(timer);
    }

    // 再試行前に少し待つ（電波の回復を待つ）
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
    }
  }

  throw lastError;
}

// ------------------------------------------------------------
// 冪等キーの管理
// ------------------------------------------------------------

/**
 * 「同じ解答の再送」には同じ冪等キーを使い、
 * 「解答を書き換えての再送信」には新しいキーを使う。
 *
 * 送信が成功したら破棄する。送信中に通信が切れて画面が
 * 再送した場合、サーバーは冪等キーで重複と判定し、二重に減点しない。
 */
const pendingKeys = new Map<string, string>();

/**
 * 冪等キーの退避先。
 *
 * メモリだけで持つと、送信中に固まった参加者がページを再読み込みして
 * 同じ解答を送り直したときに新しいキーになり、二重に減点される。
 * 電波の悪い会場で最も起きやすい操作なので、再読み込みをまたいで保つ。
 *
 * sessionStorage を選ぶのは、タブを閉じれば消えてほしいため。
 * 端末を次のイベントで使い回しても、古いキーが残らない。
 * プライベートモードなど例外を投げる環境があるので読み書きは握りつぶし、
 * 保存できなくてもメモリだけで従来どおり動くようにする。
 */
const STORAGE_KEY = "nz_pending_idempotency";

function loadPendingKeys(): void {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return;
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "string") pendingKeys.set(k, v);
    }
  } catch {
    /* 読めなければメモリだけで動かす */
  }
}

function savePendingKeys(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(pendingKeys)));
  } catch {
    /* 保存できなくても送信は続行する */
  }
}

loadPendingKeys();

function newUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // 古い環境向けのフォールバック
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function idempotencyKeyFor(questionId: string, answerSignature: string): string {
  const cacheKey = `${questionId}::${answerSignature}`;
  const existing = pendingKeys.get(cacheKey);
  if (existing !== undefined) return existing;
  const key = newUuid();
  pendingKeys.set(cacheKey, key);
  savePendingKeys();
  return key;
}

export function releaseIdempotencyKey(questionId: string, answerSignature: string): void {
  pendingKeys.delete(`${questionId}::${answerSignature}`);
  savePendingKeys();
}

/** テスト用に内部状態を初期化する */
export function resetIdempotencyKeys(): void {
  pendingKeys.clear();
  savePendingKeys();
}

// ------------------------------------------------------------
// エンドポイント
// ------------------------------------------------------------

export const api = {
  join(secret: string, token: string, onBusy?: () => void): Promise<JoinResponse> {
    return request<JoinResponse>("/api/join", {
      method: "POST",
      body: { secret, token },
      // 参加はやり直しが効くため再試行する
      retries: 2,
      // 開始の合図で一斉に読み取ると混雑しうる。ここで諦めると参加者が
      // 取り残されるため、待って自動的にやり直す
      retryOnBusy: true,
      ...(onBusy ? { onBusy } : {}),
    });
  },

  setDisplayName(displayName: string): Promise<{ participant: ParticipantView }> {
    return request<{ participant: ParticipantView }>("/api/me/name", {
      method: "POST",
      body: { displayName },
    });
  },

  me(): Promise<MeResponse> {
    return request<MeResponse>("/api/me");
  },

  questions(): Promise<QuestionsResponse> {
    return request<QuestionsResponse>("/api/questions");
  },

  score(): Promise<ScoreResponse> {
    return request<ScoreResponse>("/api/score");
  },

  submitAnswer(input: {
    questionId: string;
    answer?: string;
    selectedChoiceIds?: string[];
    idempotencyKey: string;
  }): Promise<AnswerResponse> {
    return request<AnswerResponse>("/api/answers", {
      method: "POST",
      body: input,
      // 冪等キーがあるため、送信の再試行は安全
      retries: 1,
      timeoutMs: 15_000,
    });
  },

  openHint(hintId: string): Promise<HintResponse> {
    return request<HintResponse>(`/api/hints/${encodeURIComponent(hintId)}/open`, {
      method: "POST",
      // ヒント開封もユニーク制約で二重減点しないため再試行できる
      retries: 1,
    });
  },
};
